/**
 * coalition.recent_feed — last N posts from the active workspace's
 * feed. Useful in coalition workspaces (where the feed is the central
 * surface) and also in firm dashboards if the firm has its own feed
 * activity.
 *
 * Each post collapses to author org name + body excerpt + age.
 * Click-through opens /coalition-feed.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { WidgetProps } from "../registry";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "../../components/ui";

function timeAgo(iso?: string): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`;
  if (ms < 604_800_000) return `${Math.floor(ms / 86_400_000)}d`;
  return iso.slice(0, 10);
}

function plainExcerpt(md: string, max: number): string {
  if (!md) return "";
  // Strip @[Display](type:id) → Display, then strip markdown markers
  const text = md
    .replace(/@\[([^\]]+)\]\([a-z]+:[^)]+\)/g, "$1")
    .replace(/[*`_>#-]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? text.slice(0, max - 1) + "…" : text;
}

export function CoalitionRecentFeed({ spec, workspaceId }: WidgetProps) {
  const limit = (spec.params?.limit as number) ?? 6;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "coalition.recent_feed", workspaceId, limit],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/feed?limit=${limit}`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Coalition feed could not be loaded." retry={refetch} />;
  if (isLoading) {
    return (
      <div role="status" className="space-y-2">
        <span className="sr-only">Loading…</span>
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    );
  }
  const posts: any[] = data?.posts ?? [];
  if (posts.length === 0) {
    return <div className="text-stone-500 text-xs">No recent posts.</div>;
  }

  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-baseline justify-between border-b border-stone-100 pb-1.5">
        <div className="text-stone-700 font-semibold">{posts.length} recent post{posts.length === 1 ? "" : "s"}</div>
        <Link to="/coalition-feed" className="inline-flex items-center gap-1 font-medium text-action hover:underline text-[11px]">Open feed <ArrowRight size={11} aria-hidden="true" /></Link>
      </div>
      <ul className="space-y-2">
        {posts.slice(0, limit).map((p: any) => {
          const orgName = p.author_org?.display_name || p.author_org?.name || p.author?.name || "Unknown";
          const isAuto = p.kind === "auto_event";
          return (
            <li key={p.id} className="flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5 text-[11px]">
                <span className={`px-1 rounded text-[9px] uppercase tracking-wider font-bold ${isAuto ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"}`}>
                  {isAuto ? "auto" : p.kind}
                </span>
                <span className="font-semibold text-stone-700 truncate">{orgName}</span>
                <span className="ml-auto text-stone-500">{timeAgo(p.created_at)}</span>
              </div>
              {p.body_md && (
                <div className="text-[12px] text-stone-600 leading-snug line-clamp-2">{plainExcerpt(p.body_md, 140)}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
